import { useState, useMemo } from 'react';
import { useTranslation } from '../../components/Translation';
import {
  formatPublicationDate,
  getPublicationAuthors,
  getPublicationTags,
  getPublicationTitle,
  getPublicationTypeTranslationKey,
  Publication,
  spreadsheetPublications,
} from '../../constants/publications/publications';
import PublicationModal from './PublicationsModal';

const legacyPublications: Publication[] = [
  {
    id: "pub-001",
    titleKey: "pub1_title",
    authorsKey: "pub1_authors",
    abstractKey: "pub1_abstract",
    tagsKey: "pub1_tags",
    date: "2024-01-15",
    type: "article",
    category: "wearable",
    pdfPath: "/pdf/publications/Ultrasonic_wearable.pdf",
  },
  {
    id: "pub-002",
    titleKey: "pub2_title",
    authorsKey: "pub2_authors",
    abstractKey: "pub2_abstract",
    tagsKey: "pub2_tags",
    date: "2023-10-15",
    type: "report",
    category: "fpga",
    pdfPath: "/pdf/publications/Reporte_FPGA_Farm.pdf",
  },
  {
    id: "pub-003",
    titleKey: "pub3_title",
    authorsKey: "pub3_authors",
    abstractKey: "pub3_abstract",
    tagsKey: "pub3_tags",
    date: "2024-05-20",
    type: "workshop",
    category: "sensing",
    pdfPath: "/pdf/publications/IRAP2024_Smart_Sensing.pdf",
  },
  {
    id: "pub-004",
    titleKey: "pub4_title",
    authorsKey: "pub4_authors",
    abstractKey: "pub4_abstract",
    tagsKey: "pub4_tags",
    date: "2022-01-09",
    type: "article",
    category: "sensing",
    venue: "Sensors",
    doi: "https://doi.org/10.3390/s22020484",
    pdfPath: "/pdf/publications/sensors-22-00484-v2.pdf",
  },
  {
    id: "pub-005",
    titleKey: "pub5_title",
    authorsKey: "pub5_authors",
    abstractKey: "pub5_abstract",
    tagsKey: "pub5_tags",
    date: "2020-10-26",
    type: "article",
    category: "fpga",
    venue: "IEEE Access",
    doi: "https://doi.org/10.1109/ACCESS.2020.3033718",
    pdfPath: "/pdf/publications/FPGA_Kalman_MPPT_IEEE_Access_2020.pdf.pdf",
  },
];

const publications = [...legacyPublications, ...spreadsheetPublications].sort((a, b) => {
  if (!a.date) return b.date ? 1 : 0;
  if (!b.date) return -1;
  return b.date.localeCompare(a.date);
});

const categories = [
  { key: 'all', labelKey: 'pub_cat_all' },
  { key: 'article', labelKey: 'type_article' },
  { key: 'conference-paper', labelKey: 'type_conference' },
  { key: 'chapter', labelKey: 'type_chapter' },
  { key: 'preprint', labelKey: 'type_preprint' },
  { key: 'report', labelKey: 'type_report' },
  { key: 'workshop', labelKey: 'type_workshop' },
  { key: 'thesis-doctoral', labelKey: 'type_thesis_doctoral' },
  { key: 'thesis-masters', labelKey: 'type_thesis_masters' },
  { key: 'dataset', labelKey: 'type_dataset' },
];

const PAGE_SIZE = 12;

function normalizeSearchText(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export default function PublicationsPage() {
  const { language, t } = useTranslation();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Publication | null>(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const normalizedQuery = normalizeSearchText(query);

  const filtered = useMemo(() =>
    publications.filter(p => {
      const matchesCategory = activeCategory === 'all' || p.type === activeCategory;
      const text = [
        getPublicationTitle(p, t),
        getPublicationAuthors(p, t),
        p.abstract ?? (p.abstractKey ? t(`publicaciones.${p.abstractKey}`) : ''),
        p.tagsKey ? t(`publicaciones.${p.tagsKey}`) : '',
        getPublicationTags(p, t).join(' '),
        p.venue ?? '',
        p.publicationDetails ?? '',
        p.doi ?? '',
        p.publicAccessUrl ?? '',
        p.date ?? '',
        p.id,
        t(`publicaciones.${getPublicationTypeTranslationKey(p.type)}`),
      ].map(normalizeSearchText).join(' ');
      const matchesQuery = normalizedQuery
        .split(/\s+/)
        .filter(Boolean)
        .every(term => text.includes(term));
      return matchesCategory && matchesQuery;
    }), [normalizedQuery, t, activeCategory]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const activePage = Math.min(currentPage, Math.max(totalPages, 1));
  const pageStart = (activePage - 1) * PAGE_SIZE;
  const pagePublications = filtered.slice(pageStart, pageStart + PAGE_SIZE);
  const resultSummary = t('publicaciones.resultados')
    .replace('{start}', String(filtered.length ? pageStart + 1 : 0))
    .replace('{end}', String(Math.min(pageStart + PAGE_SIZE, filtered.length)))
    .replace('{total}', String(filtered.length));

  function updateQuery(value: string) {
    setQuery(value);
    setCurrentPage(1);
  }

  function updateCategory(category: string) {
    setActiveCategory(category);
    setCurrentPage(1);
  }

  function updatePage(page: number) {
    setCurrentPage(page);
    document.getElementById('publications-results')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  return (
    <div className="min-h-screen" style={{
      background: "linear-gradient(135deg, #f0f4f8 0%, #e8edf5 50%, #f0f4f8 100%)"
    }}>
      {/* Header */}
      <div className="bg-azul-uady text-white py-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 70% 50%, #c89211 0%, transparent 50%)'
        }} />
        <div className="max-w-5xl mx-auto relative z-10">
          <span className="inline-flex items-center gap-2 bg-dorado-uady/20 border border-dorado-uady/40 text-dorado-uady text-micro font-semibold px-3 py-1 rounded-full mb-4">
            {t('publicaciones.badge_seccion')}
          </span>
          <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4">
            {t('publicaciones.titulo')}
          </h1>
          <p className="text-white/60 text-mini leading-relaxed max-w-xl">
            {t('publicaciones.subtitulo')}
          </p>
        </div>
      </div>

      {/* Filtros y búsqueda */}
      <div className="max-w-5xl mx-auto px-6 pt-10 space-y-4">
        <div className="relative">
          <label htmlFor="publications-search" className="sr-only">
            {t('publicaciones.buscar')}
          </label>
          <input
            id="publications-search"
            type="search"
            placeholder={t('publicaciones.buscar_placeholder')}
            value={query}
            onChange={e => updateQuery(e.target.value)}
            className="w-full px-5 py-3 pr-12 rounded-xl border border-gray-200 shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-azul-uady/30"
          />
          {query && (
            <button
              type="button"
              onClick={() => updateQuery('')}
              aria-label={t('publicaciones.limpiar_busqueda')}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-azul-uady focus:outline-none focus:ring-2 focus:ring-azul-uady/30"
            >
              <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className="w-4 h-4">
                <path d="m5 5 10 10M15 5 5 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('publicaciones.filtrar_tipo')}>
          {categories.map(cat => (
            <button
              key={cat.key}
              type="button"
              onClick={() => updateCategory(cat.key)}
              aria-pressed={activeCategory === cat.key}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-azul-uady/30 ${
                activeCategory === cat.key
                  ? 'bg-azul-uady text-white'
                  : 'bg-white text-gray-500 border border-gray-200 hover:border-azul-uady hover:text-azul-uady'
              }`}
            >
              {t(`publicaciones.${cat.labelKey}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Cards */}
      <div id="publications-results" className="max-w-5xl mx-auto px-6 py-10 scroll-mt-6">
        <p className="text-sm text-gray-500 mb-5" aria-live="polite" aria-atomic="true">
          {resultSummary}
        </p>
        {filtered.length === 0 ? (
          <div className="text-center text-gray-500 mt-16">
            <p>
              {query
                ? `${t('publicaciones.sin_resultados')} “${query}”`
                : t('publicaciones.sin_resultados_filtros')}
            </p>
            {(query || activeCategory !== 'all') && (
              <button
                type="button"
                onClick={() => {
                  updateQuery('');
                  updateCategory('all');
                }}
                className="mt-3 text-sm font-semibold text-azul-uady underline underline-offset-4 hover:text-dorado-uady"
              >
                {t('publicaciones.limpiar_filtros')}
              </button>
            )}
          </div>
        ) : (
          <>
            <Pagination currentPage={activePage} totalPages={totalPages} onPageChange={updatePage} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {pagePublications.map(pub => (
                <PublicationCard key={pub.id} pub={pub} language={language} onClick={() => setSelected(pub)} />
              ))}
            </div>
            {totalPages > 1 && (
              <div className="mt-8">
                <Pagination currentPage={activePage} totalPages={totalPages} onPageChange={updatePage} />
              </div>
            )}
          </>
        )}
      </div>

      {selected && (
        <PublicationModal pub={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation();
  if (totalPages <= 1) return null;

  const visiblePageCount = Math.min(totalPages, 5);
  const firstPage = Math.min(
    Math.max(currentPage - Math.floor(visiblePageCount / 2), 1),
    totalPages - visiblePageCount + 1,
  );
  const pages = Array.from({ length: visiblePageCount }, (_, index) => firstPage + index);

  return (
    <nav aria-label={t('publicaciones.paginacion')} className="flex flex-wrap items-center justify-center gap-2">
      <button
        type="button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-azul-uady transition-colors hover:border-azul-uady focus:outline-none focus:ring-2 focus:ring-azul-uady/30 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {t('publicaciones.anterior')}
      </button>
      {firstPage > 1 && (
        <>
          <PageButton page={1} currentPage={currentPage} onPageChange={onPageChange} />
          {firstPage > 2 && <span aria-hidden="true" className="px-1 text-gray-400">…</span>}
        </>
      )}
      {pages.map(page => (
        <PageButton key={page} page={page} currentPage={currentPage} onPageChange={onPageChange} />
      ))}
      {firstPage + visiblePageCount - 1 < totalPages && (
        <>
          {firstPage + visiblePageCount < totalPages && <span aria-hidden="true" className="px-1 text-gray-400">…</span>}
          <PageButton page={totalPages} currentPage={currentPage} onPageChange={onPageChange} />
        </>
      )}
      <button
        type="button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-azul-uady transition-colors hover:border-azul-uady focus:outline-none focus:ring-2 focus:ring-azul-uady/30 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {t('publicaciones.siguiente')}
      </button>
    </nav>
  );
}

function PageButton({
  page,
  currentPage,
  onPageChange,
}: {
  page: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation();
  const isCurrentPage = page === currentPage;

  return (
    <button
      type="button"
      onClick={() => onPageChange(page)}
      aria-label={`${t('publicaciones.pagina')} ${page}`}
      aria-current={isCurrentPage ? 'page' : undefined}
      className={`min-w-10 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-azul-uady/30 ${
        isCurrentPage
          ? 'border-azul-uady bg-azul-uady text-white'
          : 'border-gray-200 bg-white text-azul-uady hover:border-azul-uady'
      }`}
    >
      {page}
    </button>
  );
}

function PublicationCard({ pub, language, onClick }: { pub: Publication; language: string; onClick: () => void }) {
  const { t } = useTranslation();
  const tags = getPublicationTags(pub, t);
  const pdfUrl = pub.pdfPath ?? pub.pdfUrl;
  const date = formatPublicationDate(pub.date, language, 'short');

  return (
    <div
      onClick={onClick}
      className="group cursor-pointer rounded-2xl overflow-hidden border border-white/60 flex flex-col h-[420px] hover:scale-[1.02] hover:shadow-xl transition-all duration-300"
      style={{
        background: "rgba(255,255,255,0.7)",
        backdropFilter: "blur(12px)",
        boxShadow: "0 4px 24px rgba(13,31,53,0.08), inset 0 1px 0 rgba(255,255,255,0.8)"
      }}
    >
      <div className="p-5 flex flex-col flex-1 min-h-0">
        <div className="shrink-0">
          <div className="flex flex-wrap gap-1 mb-3">
            <span className="text-micro font-bold uppercase text-azul-uady tracking-widest">
              {t(`publicaciones.${getPublicationTypeTranslationKey(pub.type)}`)}
            </span>
            {tags.map(tag => (
              <span key={tag} className="text-micro font-bold uppercase text-dorado-uady tracking-widest">
                {tag}
              </span>
            ))}
          </div>
          <h3 className="text-base font-bold text-azul-uady mb-3 leading-snug line-clamp-3">
            {getPublicationTitle(pub, t)}
          </h3>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
          {date && <p className="text-micro text-gray-400 mb-2">{date}</p>}
          {getPublicationAuthors(pub, t) && (
            <p className="text-mini text-gray-500 mb-2 line-clamp-2 italic">
              {getPublicationAuthors(pub, t)}
            </p>
          )}
          {(pub.venue || pub.publicationDetails) && (
            <p className="text-micro text-gray-400 mb-2 line-clamp-2">
              {[pub.venue, pub.publicationDetails].filter(Boolean).join(' · ')}
            </p>
          )}
          {(pub.abstract || pub.abstractKey) && (
            <div
              role="region"
              aria-label={`${t('publicaciones.descripcion')}: ${getPublicationTitle(pub, t)}`}
              tabIndex={0}
              onClick={e => e.stopPropagation()}
              className="pr-1 text-mini text-gray-600 leading-relaxed text-justify transition-colors group-hover:font-semibold group-focus-within:font-semibold focus:outline-none focus:ring-2 focus:ring-azul-uady/30"
            >
              {pub.abstract ?? (pub.abstractKey ? t(`publicaciones.${pub.abstractKey}`) : '')}
            </div>
          )}
        </div>
        <div className="flex flex-wrap shrink-0 items-center justify-between mt-4 pt-3 border-t border-gray-100 gap-2">
          {pdfUrl && (
            <a href={pdfUrl} download={Boolean(pub.pdfPath)} target={pub.pdfPath ? undefined : "_blank"}
              rel={pub.pdfPath ? undefined : "noopener noreferrer"} onClick={e => e.stopPropagation()}
              className="text-micro font-semibold text-gray-400 hover:text-dorado-uady transition-colors">
              {t('publicaciones.descargar')}
            </a>
          )}
          {pub.doi && (
            <a
              href={pub.doi}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              aria-label={`${t('publicaciones.ver_articulo')} (DOI)`}
              className="inline-flex items-center gap-1 text-micro font-semibold text-azul-uady hover:text-dorado-uady transition-colors"
            >
              <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className="w-4 h-4">
                <path d="M11.25 3.75h5v5M16 4l-7.5 7.5M15 10.75v4.5a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              DOI
            </a>
          )}
          {pub.publicAccessUrl && (
            <a
              href={pub.publicAccessUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="text-micro font-semibold text-azul-uady hover:text-dorado-uady transition-colors"
            >
              {t('publicaciones.acceso_abierto')}
            </a>
          )}
          {pub.researchGateUrl && (
            <a
              href={pub.researchGateUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="text-micro font-semibold text-gray-500 hover:text-azul-uady transition-colors"
            >
              {pub.researchGateFullTextAvailable
                ? t('publicaciones.rg_full_text')
                : t('publicaciones.view_rg')}
            </a>
          )}
          <button
            onClick={onClick}
            className="ml-auto text-micro font-semibold text-azul-uady hover:text-dorado-uady transition-colors"
          >
            {t('publicaciones.ver_mas')}
          </button>
        </div>
      </div>
    </div>
  );
}